import type { TimeScope, TopTestRow, TopTestsMode } from '#shared/types';
import { formatCount, formatMoney } from '#client/lib/format';

/** "Good morning" until noon, "Good afternoon" until 5, then "Good evening". */
export function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/** The page subtitle: the weekday and the date in full. */
export function formatLongDate(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function sumValues(values: readonly number[]): number {
  return values.reduce((sum, value) => sum + value, 0);
}

interface TopTestsQuery {
  scope: TimeScope;
  by: TopTestsMode;
  paidOnly: boolean;
}

export function topTestsPath({ scope, by, paidOnly }: TopTestsQuery): string {
  const params = new URLSearchParams({ scope, by, limit: '5' });
  if (paidOnly) params.set('paid_only', 'true');
  return `/dashboard/top-tests?${params.toString()}`;
}

type TopTestFigures = Pick<TopTestRow, 'count' | 'revenue'>;

/** The figure a row is ranked on: money when ranked by amount and visible, else the count. */
export function topTestValue(row: TopTestFigures, by: TopTestsMode): string {
  return by === 'amount' && row.revenue !== null
    ? formatMoney(row.revenue)
    : `${formatCount(row.count)}×`;
}

/**
 * The other measure, muted: five counts with no money beside them answers half
 * the question the card was opened for. Null when that measure is withheld.
 */
export function topTestAside(row: TopTestFigures, by: TopTestsMode): string | null {
  if (by === 'amount') return `${formatCount(row.count)} ordered`;
  return row.revenue === null ? null : `${formatMoney(row.revenue)} billed`;
}
